import type { IconButtonProps } from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

import { mergeClasses, varAlpha } from '@src/utils';

import { Iconify } from '@components/Iconify';
import { uploadClasses } from '../classes';
import type { SingleFilePreviewProps } from '../types';

const PreviewRoot = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  position: 'absolute',
  padding: theme.spacing(1),
  '& > img': {
    width: '100%',
    height: '100%',
    objectFit: 'scale-down',
    borderRadius: theme.shape.borderRadius,
  },
}));

export function DeleteButton({ sx, ...other }: IconButtonProps) {
  return (
    <IconButton
      size="small"
      aria-label="Remove file"
      sx={[
        (theme) => ({
          top: 16,
          right: 16,
          zIndex: 9,
          position: 'absolute',
          color: varAlpha(theme.vars.palette.common.whiteChannel, 0.8),
          bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.72),
          '&:hover': { bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.48) },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Iconify icon="mingcute:close-line" width={18} />
    </IconButton>
  );
}

export function SingleFilePreview({ file, sx, className, ...other }: SingleFilePreviewProps) {
  const fileName = typeof file === 'string' ? file : file.name;
  const isPdf = fileName.toLowerCase().endsWith('.pdf');

  const previewUrl = typeof file === 'string' ? file : URL.createObjectURL(file);

  return (
    <PreviewRoot className={mergeClasses([uploadClasses.uploadSinglePreview, className])} sx={sx} {...other}>
      {isPdf ? (
        <iframe
          title={fileName}
          src={`${previewUrl}#toolbar=0`}
          width="100%"
          height="100%"
          style={{ border: 'none', borderRadius: 'inherit' }}
        />
      ) : (
        <img alt={fileName} src={previewUrl} />
      )}
    </PreviewRoot>
  );
}
